/**
 * Premium Session Recording REST client (Sessions API, Cognito bearer auth).
 *
 * Provider routes require the admin switch + assigned provider / L4 (the
 * server re-checks every call). `getSessionRecordingMemberStatus` is the
 * content-free view for members and followers.
 *
 * UI components go through the feature hooks / adapters, not this module.
 */

import {
  sessionRecordingPaths,
  type ApproveSessionRecordingRequest,
  type CompleteSessionRecordingUploadRequest,
  type ConsentClipUploadSlot,
  type CreateConsentRequestBody,
  type CreateSessionRecordingRequest,
  type RequestConsentClipUploadRequest,
  type RespondConsentRequestBody,
  type SessionRecordingConsentAudio,
  type SessionRecordingConsentRequestForRecipient,
  type SessionRecordingConsentRequestList,
  type SessionRecordingConsentRequestSummary,
  type RegenerateSessionRecordingNoteRequest,
  type SaveSessionRecordingNoteRequest,
  type SessionRecordingAction,
  type SessionRecordingAudioUrl,
  type SessionRecordingDetail,
  type SessionRecordingEventInput,
  type SessionRecordingMemberStatus,
  type SessionRecordingSummary,
  type SessionRecordingUploadPlan,
  type StartSessionRecordingUploadRequest,
  type WithdrawSessionRecordingRequest,
} from '@shared/sessions/session-recording'
import { getSessionsApiBaseUrl, getSessionsAuthToken } from '../../sessions/data/sessions-api-env'

/**
 * Carries the HTTP status and the server's error code so callers can tell
 * retryable (network / 5xx / 401 refresh) from permanent (4xx) failures and
 * show the server's human-readable `message`.
 */
export class SessionRecordingApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(message: string, status: number, code: string | null = null) {
    super(message)
    this.name = 'SessionRecordingApiError'
    this.status = status
    this.code = code
  }

  /** Network failures (status 0), auth refresh, throttling and 5xx are worth retrying. */
  get retryable(): boolean {
    return this.status === 0 || this.status === 401 || this.status === 429 || this.status >= 500
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getSessionsAuthToken()
  if (!token) throw new SessionRecordingApiError('Not signed in', 401)
  let res: Response
  try {
    res = await fetch(`${getSessionsApiBaseUrl()}${path}`, {
      ...init,
      headers: {
        Authorization: token,
        ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    })
  } catch {
    throw new SessionRecordingApiError('No connection — we’ll try again', 0)
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: unknown; message?: unknown } | null
    const message =
      typeof body?.message === 'string' && body.message
        ? body.message
        : `Request failed (${res.status})`
    throw new SessionRecordingApiError(
      message,
      res.status,
      typeof body?.error === 'string' ? body.error : null,
    )
  }
  return (await res.json()) as T
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })

const action = (sessionUuid: string, recordingId: string, name: SessionRecordingAction) =>
  sessionRecordingPaths.action(sessionUuid, recordingId, name)

/** Where to PUT one spoken-consent clip; send the returned `clipId` in the consent. */
export function requestConsentClipUpload(
  sessionUuid: string,
  body: RequestConsentClipUploadRequest,
): Promise<ConsentClipUploadSlot> {
  return post(sessionRecordingPaths.consentClip(sessionUuid), body)
}

/** PUT the clip bytes to the presigned slot (no auth header — the URL is signed). */
export async function putConsentClip(slot: ConsentClipUploadSlot, body: Blob): Promise<void> {
  let res: Response
  try {
    res = await fetch(slot.url, { method: 'PUT', headers: { 'Content-Type': slot.contentType }, body })
  } catch {
    throw new SessionRecordingApiError('No connection — we’ll try again', 0)
  }
  if (!res.ok) throw new SessionRecordingApiError(`Upload failed (${res.status})`, res.status)
}

/** Upload one recorded answer before create; resolves to the clip id the consent carries. */
export async function uploadConsentClip(
  sessionUuid: string,
  clip: { blob: Blob; contentType: ConsentClipUploadSlot['contentType']; durationSec: number },
): Promise<string> {
  const slot = await requestConsentClipUpload(sessionUuid, {
    contentType: clip.contentType,
    sizeBytes: clip.blob.size,
    durationSec: clip.durationSec,
  })
  await putConsentClip(slot, clip.blob)
  return slot.clipId
}

/** Who can be asked in their app for this session's member, and this provider's requests. */
export function listConsentRequests(sessionUuid: string): Promise<SessionRecordingConsentRequestList> {
  return request(sessionRecordingPaths.consentRequests(sessionUuid))
}

/** Ask (or ask again): a push to their app + a row in their Alerts. */
export function sendConsentRequest(
  sessionUuid: string,
  body: CreateConsentRequestBody,
): Promise<{ request: SessionRecordingConsentRequestSummary; delivered: boolean }> {
  return post(sessionRecordingPaths.consentRequests(sessionUuid), body)
}

/** The provider's poll for one request. */
export function getConsentRequestStatus(
  sessionUuid: string,
  requestId: string,
): Promise<{ request: SessionRecordingConsentRequestSummary }> {
  return request(sessionRecordingPaths.consentRequests(sessionUuid, requestId))
}

/** The person asked: the request with the session details for the accept screen. */
export function getConsentRequestForRecipient(
  sessionUuid: string,
  requestId: string,
): Promise<SessionRecordingConsentRequestForRecipient> {
  return request(sessionRecordingPaths.consentRequests(sessionUuid, requestId))
}

/** The person asked: Accept or Decline. */
export function respondToConsentRequest(
  sessionUuid: string,
  body: RespondConsentRequestBody,
): Promise<SessionRecordingConsentRequestForRecipient> {
  return post(sessionRecordingPaths.consentRequests(sessionUuid), body)
}

/** Short-lived playback URLs for the spoken-consent clips a recording keeps. */
export function getSessionRecordingConsentAudio(
  sessionUuid: string,
  recordingId: string,
): Promise<SessionRecordingConsentAudio> {
  return request(action(sessionUuid, recordingId, 'consent-audio'))
}

/** Create a recording with the consent captured on this device (call before starting capture). */
export function createSessionRecording(
  sessionUuid: string,
  body: CreateSessionRecordingRequest,
): Promise<{ recording: SessionRecordingSummary }> {
  return post(sessionRecordingPaths.collection(sessionUuid), body)
}

export function listSessionRecordings(
  sessionUuid: string,
): Promise<{ items: SessionRecordingSummary[] }> {
  return request(sessionRecordingPaths.collection(sessionUuid))
}

export function getSessionRecording(
  sessionUuid: string,
  recordingId: string,
): Promise<SessionRecordingDetail> {
  return request(sessionRecordingPaths.one(sessionUuid, recordingId))
}

/** Content-free status (member, follower or provider). */
export function getSessionRecordingMemberStatus(
  sessionUuid: string,
): Promise<{ items: SessionRecordingMemberStatus[] }> {
  return request(sessionRecordingPaths.memberStatus(sessionUuid))
}

/** Capture events (pause/resume/stop…) — content-free audit + member banner state. */
export function postSessionRecordingEvent(
  sessionUuid: string,
  recordingId: string,
  event: SessionRecordingEventInput,
): Promise<{ recording: SessionRecordingSummary }> {
  return post(action(sessionUuid, recordingId, 'events'), event)
}

/** Start (or resume) the multipart upload: returns presigned part URLs + parts already uploaded. */
export function startSessionRecordingUpload(
  sessionUuid: string,
  recordingId: string,
  body: StartSessionRecordingUploadRequest,
): Promise<SessionRecordingUploadPlan> {
  return post(action(sessionUuid, recordingId, 'upload'), body)
}

export function completeSessionRecordingUpload(
  sessionUuid: string,
  recordingId: string,
  body: CompleteSessionRecordingUploadRequest,
): Promise<{ recording: SessionRecordingSummary }> {
  return post(action(sessionUuid, recordingId, 'complete'), body)
}

export function saveSessionRecordingNote(
  sessionUuid: string,
  recordingId: string,
  body: SaveSessionRecordingNoteRequest,
): Promise<SessionRecordingDetail> {
  return request(action(sessionUuid, recordingId, 'note'), {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function regenerateSessionRecordingNote(
  sessionUuid: string,
  recordingId: string,
  body: RegenerateSessionRecordingNoteRequest,
): Promise<{ recording: SessionRecordingSummary }> {
  return post(action(sessionUuid, recordingId, 'regenerate'), body)
}

/** Re-read the session's GPS stops into the AHP note's places and Visited places text (unapproved drafts). */
export function refreshSessionRecordingPlaces(
  sessionUuid: string,
  recordingId: string,
): Promise<SessionRecordingDetail> {
  return post(action(sessionUuid, recordingId, 'places'), {})
}

/** Re-run the failed stage (transcription while audio exists, or drafting). */
export function retrySessionRecording(
  sessionUuid: string,
  recordingId: string,
): Promise<{ recording: SessionRecordingSummary }> {
  return post(action(sessionUuid, recordingId, 'retry'))
}

/** Approve the note (locks it and deletes the audio). */
export function approveSessionRecording(
  sessionUuid: string,
  recordingId: string,
  body: ApproveSessionRecordingRequest,
): Promise<SessionRecordingDetail> {
  return post(action(sessionUuid, recordingId, 'approve'), body)
}

/** Consent withdrawn: stop and delete audio, transcript and note (content-free tombstone kept). */
export function withdrawSessionRecording(
  sessionUuid: string,
  recordingId: string,
  body: WithdrawSessionRecordingRequest,
): Promise<{ recording: SessionRecordingSummary }> {
  return post(action(sessionUuid, recordingId, 'withdraw'), body)
}

/** Provider discards before approval (deletes audio, transcript and note). */
export function discardSessionRecording(
  sessionUuid: string,
  recordingId: string,
): Promise<{ recording: SessionRecordingSummary }> {
  return request(sessionRecordingPaths.one(sessionUuid, recordingId), { method: 'DELETE' })
}

/** Short-lived playback URL while the audio still exists (before approval / day 7). */
export function getSessionRecordingAudioUrl(
  sessionUuid: string,
  recordingId: string,
): Promise<SessionRecordingAudioUrl> {
  return request(action(sessionUuid, recordingId, 'audio'))
}
